import React, { useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import Card from '../../UI/Card';
import { useEPMContext } from '../../../contexts/EPMContext';
import TimeframeDropdown from './TimeframeDropdown';
import TagBreakdown from './TagBreakdown';
import {
    OUTCOMES,
    OUTCOME_STYLES,
    getVerdictOutcome,
    isTestInTimeframe,
} from './utils';

const ProgrammeHealth = () => {
    const { tests = [], tagsMap = {} } = useEPMContext();
    const [showTags, setShowTags] = useState(true);
    const [selectedTimeframe, setSelectedTimeframe] = useState('4_quarters');

    const filteredTests = useMemo(() => {
        return tests.filter((test) => isTestInTimeframe(test, selectedTimeframe));
    }, [tests, selectedTimeframe]);

    const { concludedCount, metrics, outcomePercentages, outcomeLabel } = useMemo(() => {
        const concludedTests = filteredTests.filter((test) => test?.status === 'CONCLUDED');
        const count = concludedTests.length;

        const outcomeCounts = { win: 0, loss: 0, inconclusive: 0, invalid: 0 };
        concludedTests.forEach((test) => {
            const outcome = getVerdictOutcome(test);
            if (outcome) {
                outcomeCounts[outcome] += 1;
            }
        });

        const winRate = count ? Math.round((outcomeCounts.win / count) * 100) : 0;
        const lossRate = count ? Math.round((outcomeCounts.loss / count) * 100) : 0;
        const inconclusiveRate = count ? Math.round((outcomeCounts.inconclusive / count) * 100) : 0;
        const invalidRate = count ? Math.round((outcomeCounts.invalid / count) * 100) : 0;

        const computedMetrics = [
            [String(count), 'Concluded'],
            [`${winRate}%`, 'Win rate'],
            [`${invalidRate}%`, 'Invalid'],
        ];

        const percentages = {
            win: count ? (outcomeCounts.win / count) * 100 : 0,
            loss: count ? (outcomeCounts.loss / count) * 100 : 0,
            inconclusive: count ? (outcomeCounts.inconclusive / count) * 100 : 0,
            invalid: count ? (outcomeCounts.invalid / count) * 100 : 0,
        };

        const label = count > 0
            ? `Win ${winRate}% · loss ${lossRate}% · inconclusive ${inconclusiveRate}% · invalid ${invalidRate}%`
            : 'No concluded tests yet';

        return {
            concludedCount: count,
            metrics: computedMetrics,
            outcomePercentages: percentages,
            outcomeLabel: label,
        };
    }, [filteredTests]);

    return (
        <Card variant="default" padding="md" className="epm-section mb-6">
            <div className="flex items-center justify-between gap-5 mb-6">
                <h2 className="m-0 text-base md:text-lg font-bold text-stride-heading">Programme health</h2>
                <TimeframeDropdown
                    selected={selectedTimeframe}
                    onChange={setSelectedTimeframe}
                />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3.5 mb-6">
                {metrics.map(([value, label]) => (
                    <div
                        key={label}
                        className="p-4 rounded-xl bg-slate-50/80 border border-slate-100 flex flex-col justify-between"
                    >
                        <strong className="block text-2xl md:text-3xl font-extrabold leading-none text-primary mb-1.5">
                            {value}
                        </strong>
                        <span className="block text-xs text-gray-500 font-medium">
                            {label}
                        </span>
                    </div>
                ))}
            </div>

            <div
                className="flex h-3 overflow-hidden rounded-full bg-slate-100 p-0.5 border border-slate-200/60"
                aria-label={outcomeLabel}
                style={concludedCount === 0 ? { background: '#edf0f7' } : undefined}
            >
                {OUTCOMES.map((outcome) => outcomePercentages[outcome] > 0 && (
                    <span
                        key={outcome}
                        className={`block h-full rounded-sm ${OUTCOME_STYLES[outcome]}`}
                        style={{ width: `${outcomePercentages[outcome]}%` }}
                    />
                ))}
            </div>
            <p className="text-xs text-gray-500 font-medium mt-2">{outcomeLabel}</p>

            <button
                type="button"
                className="inline-flex items-center gap-1.5 mt-5 border-0 bg-transparent p-0 text-stride-heading text-xs font-semibold cursor-pointer hover:text-primary transition-colors focus:outline-none"
                onClick={() => setShowTags(!showTags)}
                aria-expanded={showTags}
            >
                <ChevronDown
                    size={14}
                    className={`text-primary transition-transform duration-150 ${showTags ? '' : '-rotate-90'}`}
                />
                Breakdown by tag
            </button>

            {showTags && <TagBreakdown tests={filteredTests} tagsMap={tagsMap} />}
        </Card>
    );
};

export default ProgrammeHealth;
